import React from 'react';

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  TextField,
  makeStyles,
  SvgIconTypeMap,
  IconButton,
} from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import ClearIcon from '@material-ui/icons/Clear';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import MovieIcon from '@material-ui/icons/Movie';
import TextFormatIcon from '@material-ui/icons/TextFormat';
import { useFormikContext } from 'formik';

import { TiposDeCabecalhoMensagemInterativaEnum } from 'store/reducers/mensagens';

interface HeaderFormValues {
  tipoCabecalho: TiposDeCabecalhoMensagemInterativaEnum;
  textoCabecalho: string;
  imagemCabecalho: string;
  videoCabecalho: string;
  documentoCabecalho: string;
}

interface MenuItemType {
  value: TiposDeCabecalhoMensagemInterativaEnum;
  label: string;
  icon: OverridableComponent<SvgIconTypeMap<Record<string, unknown>, 'svg'>>;
  fieldName: keyof HeaderFormValues;
  placeholder?: string;
}

const HEADER_TYPES_CONFIG: MenuItemType[] = [
  {
    value: TiposDeCabecalhoMensagemInterativaEnum.TEXTO,
    label: 'Texto',
    icon: TextFormatIcon,
    fieldName: 'textoCabecalho',
  },
  {
    value: TiposDeCabecalhoMensagemInterativaEnum.IMAGEM,
    label: 'Imagem',
    icon: ImageIcon,
    fieldName: 'imagemCabecalho',
    placeholder: 'https://example.com/image.jpg',
  },
  {
    value: TiposDeCabecalhoMensagemInterativaEnum.VIDEO,
    label: 'Vídeo',
    icon: MovieIcon,
    fieldName: 'videoCabecalho',
    placeholder: 'https://example.com/video.mp4',
  },
  {
    value: TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO,
    label: 'Documento',
    icon: InsertDriveFileIcon,
    fieldName: 'documentoCabecalho',
    placeholder: 'https://example.com/document.pdf',
  },
];

const FIELD_PLACEHOLDERS: Record<
  TiposDeCabecalhoMensagemInterativaEnum,
  string
> = {
  [TiposDeCabecalhoMensagemInterativaEnum.TEXTO]: 'Texto do cabeçalho',
  [TiposDeCabecalhoMensagemInterativaEnum.IMAGEM]: 'URL da imagem do cabeçalho',
  [TiposDeCabecalhoMensagemInterativaEnum.VIDEO]: 'URL do vídeo do cabeçalho',
  [TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO]:
    'URL do documento do cabeçalho',
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
  formControl: {
    maxWidth: 220,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  icon: {
    width: 16,
    height: 16,
  },
  selectDisplay: {
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    width: 200,
  },
  helperText: {
    marginLeft: 0,
    paddingTop: theme.spacing(1),
  },
  characterCounter: {
    position: 'absolute',
    bottom: 40,
    right: 15,
  },
}));

const HeaderFormFields: React.FC = () => {
  const classes = useStyles();
  const formik = useFormikContext<HeaderFormValues>();

  const getSelectedConfig = () => {
    return HEADER_TYPES_CONFIG.find(
      config => config.value === formik.values.tipoCabecalho,
    );
  };

  const getFieldName = (
    type: TiposDeCabecalhoMensagemInterativaEnum,
  ): keyof HeaderFormValues => {
    const config = HEADER_TYPES_CONFIG.find(item => item.value === type);
    return config?.fieldName || 'textoCabecalho';
  };

  const getPlaceholder = () => {
    const selectedConfig = getSelectedConfig();
    if (!selectedConfig) return '';

    return selectedConfig.placeholder || '';
  };

  const hasFieldError = () => {
    const selectedConfig = getSelectedConfig();
    if (!selectedConfig) return false;

    const { fieldName } = selectedConfig;
    return formik.touched[fieldName] && Boolean(formik.errors[fieldName]);
  };

  const getFieldError = () => {
    const selectedConfig = getSelectedConfig();
    if (!selectedConfig) return null;

    const { fieldName } = selectedConfig;
    return formik.touched[fieldName] ? formik.errors[fieldName] : null;
  };

  const getCurrentFieldValue = () => {
    const selectedConfig = getSelectedConfig();
    if (!selectedConfig) return '';

    return formik.values[selectedConfig.fieldName] || '';
  };

  const handleOnChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const newType = e.target.value as TiposDeCabecalhoMensagemInterativaEnum;
    const previousType = formik.values.tipoCabecalho;
    const previousField = getFieldName(previousType);
    formik.setFieldValue(previousField, '');
    formik.setFieldValue('tipoCabecalho', newType);
  };

  const isTextType =
    formik.values.tipoCabecalho ===
    TiposDeCabecalhoMensagemInterativaEnum.TEXTO;
  const textLength = isTextType ? formik.values.textoCabecalho?.length || 0 : 0;
  const maxTextLength = 60;

  const [hovered, setHovered] = React.useState(false);

  const onMouseEnter = () => setHovered(true);
  const onMouseLeave = () => setHovered(false);

  return (
    <Box className={classes.container}>
      <Box>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          fullWidth
        >
          <InputLabel id="tipo-cabecalho-label">Cabeçalho</InputLabel>
          <Select
            labelId="tipo-cabecalho-label"
            name="tipoCabecalho"
            label="Cabeçalho"
            value={formik.values.tipoCabecalho || ''}
            onChange={handleOnChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            endAdornment={
              formik.values.tipoCabecalho &&
              hovered && (
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    formik.setFieldValue('tipoCabecalho', '');
                  }}
                  aria-label="Limpar seleção"
                  style={{ marginRight: 10 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }
            SelectDisplayProps={{
              style: {
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: 200,
              },
            }}
          >
            {HEADER_TYPES_CONFIG.map(
              ({ value, label, icon: IconComponent }) => (
                <MenuItem
                  key={value}
                  value={value}
                  className={classes.menuItem}
                >
                  <IconComponent className={classes.icon} />
                  {label}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>

        <FormHelperText className={classes.helperText}>
          Selecione o tipo de arquivo para compor o cabeçalho de sua mensagem.
        </FormHelperText>
      </Box>

      {formik.values.tipoCabecalho && (
        <Box position="relative">
          <TextField
            name={getFieldName(formik.values.tipoCabecalho)}
            label={FIELD_PLACEHOLDERS[formik.values.tipoCabecalho]}
            variant="outlined"
            error={hasFieldError()}
            placeholder={getPlaceholder()}
            helperText={
              getFieldError() ||
              (isTextType
                ? 'Insira o texto do cabeçalho da mensagem.'
                : 'Insira a URL do arquivo para o cabeçalho.')
            }
            value={getCurrentFieldValue()}
            onChange={formik.handleChange}
            inputProps={
              isTextType
                ? { maxLength: maxTextLength, style: { marginRight: 50 } }
                : undefined
            }
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            fullWidth
          />

          {isTextType && (
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.characterCounter}
            >
              {textLength}/{maxTextLength}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HeaderFormFields;
